import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { storageService } from '../services/storage.service';
import { authService } from '../services/auth.service';
import { syncService } from '../services/sync.service';
import { SECURITY_CONFIG } from '../config/app.config';
import SplashScreen from './SplashScreen';
import PinLogin from './PinLogin';

interface AppInitializerProps {
  children: React.ReactNode;
}

type AppState = 'loading' | 'pin-required' | 'login-required' | 'ready';

// Cache token validation to avoid redundant network calls
let lastTokenValidation: { timestamp: number; valid: boolean } | null = null;
const TOKEN_VALIDATION_CACHE_MS = SECURITY_CONFIG.TOKEN_VALIDATION_CACHE_MINUTES * 60 * 1000;

/**
 * AppInitializer Component
 * Handles app startup flow:
 * 1. Show splash screen
 * 2. Check authentication status
 * 3. Show PIN login if enabled
 * 4. Show login page if not authenticated
 * 5. Initialize sync service
 * 6. Proceed to app if authenticated
 */
const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
  const [appState, setAppState] = useState<AppState>('loading');
  const navigate = useNavigate();
  const location = useLocation();
  const initRunRef = useRef(0);

  // Routes that must bypass the startup gate (otherwise you can get stuck)
  const bypassRoutes = ['/signup', '/setup-pin'];
  const isBypassRoute = bypassRoutes.includes(location.pathname);

  const initializeServices = useCallback(async () => {
    try {
      // Initialize sync service
      await syncService.initialize();
    } catch (error) {
      console.error('Service initialization error:', error);
    }
  }, []);

  const initializeApp = useCallback(
    async (opts?: { skipSplash?: boolean }) => {
      const runId = ++initRunRef.current;
      try {
        // First: auth gate (fast; avoid showing PIN if user has fully logged out)
        const isAuthenticated = await authService.isAuthenticated();
        if (!isAuthenticated) {
          if (runId !== initRunRef.current) return;
          setAppState('login-required');
          if (location.pathname !== '/login') {
            navigate('/login', { replace: true });
          }
          return;
        }

        // Quick token expiry check before showing PIN screen
        // This prevents showing PIN login with stale/expired tokens from previous installations
        const authData = await storageService.getAuthData();
        if (authData?.expires_at) {
          const expiryDate = new Date(authData.expires_at);
          const now = new Date();
          if (expiryDate <= now) {
            // Token expired - logout and require fresh login
            await authService.logout();
            if (runId !== initRunRef.current) return;
            setAppState('login-required');
            navigate('/login', { replace: true });
            return;
          }
        }

        // Second: device PIN gate (fast; do NOT delay behind splash)
        const pinEnabled = await authService.isPinEnabled();
        const isUnlockedThisSession = await storageService.isSessionUnlocked();

        // If user is on /login, allow them to pick password vs PIN explicitly.
        if (pinEnabled && !isUnlockedThisSession && location.pathname !== '/login') {
          if (runId !== initRunRef.current) return;
          setAppState('pin-required');
          return;
        }

        // Wait for splash screen only if we're not about to show PIN
        if (!opts?.skipSplash) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }

        if (runId !== initRunRef.current) return;

        const settings = await storageService.getSettings();

        // Token validation with caching to reduce network calls
        if (!settings.offlineMode && navigator.onLine) {
          const now = Date.now();
          const cacheValid = lastTokenValidation && 
                           (now - lastTokenValidation.timestamp < TOKEN_VALIDATION_CACHE_MS);
          
          if (cacheValid) {
            // Use cached result
            if (!lastTokenValidation!.valid) {
              await authService.logout();
              if (runId !== initRunRef.current) return;
              setAppState('login-required');
              navigate('/login', { replace: true });
              return;
            }
          } else {
            // Validate with server and cache result
            const tokenValid = await authService.validateTokenWithServer();
            lastTokenValidation = { timestamp: now, valid: tokenValid };
            
            if (!tokenValid) {
              await authService.logout();
              if (runId !== initRunRef.current) return;
              setAppState('login-required');
              navigate('/login', { replace: true });
              return;
            }
          }
        }

        // Show app immediately - defer non-critical operations
        if (runId !== initRunRef.current) return;
        setAppState('ready');

        // Run non-critical operations in background
        Promise.all([
          authService.ensureUserPersonRecord(undefined, SECURITY_CONFIG.SKIP_PERSON_FETCH_ON_PIN_LOGIN).catch(err => 
            console.warn('Background ensureUserPersonRecord failed:', err)
          ),
          initializeServices().catch(err => 
            console.warn('Background service initialization failed:', err)
          ),
        ]).catch(console.error);

        // If user is authenticated and session is unlocked, don't leave them on the password login screen.
        if (location.pathname === '/login') {
          const unlockedNow = await storageService.isSessionUnlocked();
          if (unlockedNow || !pinEnabled) {
            navigate('/', { replace: true });
          }
        }
      } catch (error) {
        if (runId !== initRunRef.current) return;
        console.error('App initialization error:', error);
        setAppState('login-required');
        navigate('/login', { replace: true });
      }
    },
    [initializeServices, location.pathname, navigate]
  );

  useEffect(() => {
    if (isBypassRoute) {
      setAppState('ready');
      return;
    }

    void initializeApp();
  }, [initializeApp, isBypassRoute]);

  const handlePinSuccess = async () => {
    await storageService.setSessionUnlocked(true);
    
    // Validate token before proceeding - prevents using stale tokens after PIN entry
    const authData = await storageService.getAuthData();
    if (authData?.expires_at) {
      const expiryDate = new Date(authData.expires_at);
      const now = new Date();
      if (expiryDate <= now) {
        // Token expired even though PIN was correct - require fresh login
        await authService.logout();
        setAppState('login-required');
        navigate('/login', { replace: true });
        return;
      }
    }
    
    // Fast path: if token was validated recently, skip re-validation
    const now = Date.now();
    const recentlyValidated = lastTokenValidation && 
                             (now - lastTokenValidation.timestamp < TOKEN_VALIDATION_CACHE_MS) &&
                             lastTokenValidation.valid;
    
    if (recentlyValidated) {
      // Skip full initialization, just show app immediately
      setAppState('ready');
      
      // Run background tasks
      Promise.all([
        authService.ensureUserPersonRecord(undefined, SECURITY_CONFIG.SKIP_PERSON_FETCH_ON_PIN_LOGIN).catch(err => 
          console.warn('Background ensureUserPersonRecord failed:', err)
        ),
        initializeServices().catch(err => 
          console.warn('Background service initialization failed:', err)
        ),
      ]).catch(console.error);
      
      // Handle navigation
      const pinEnabled = await authService.isPinEnabled();
      if (location.pathname === '/login') {
        const unlockedNow = await storageService.isSessionUnlocked();
        if (unlockedNow || !pinEnabled) {
          navigate('/', { replace: true });
        }
      }
    } else {
      // Re-run init (without splash) to validate token
      await initializeApp({ skipSplash: true });
    }
  };

  const handleForgotPin = async () => {
    // Forgot PIN - allow password login for this instance.
    // Do NOT disable PIN on the device.
    await authService.lock();
    setAppState('login-required');
    navigate('/login', { replace: true });
  };

  const handlePinCancel = () => {
    // User cancelled PIN entry; keep token/PIN but require login screen
    authService.lock();
    setAppState('login-required');
    navigate('/login', { replace: true });
  };

  // Splash Screen
  if (appState === 'loading') {
    return <SplashScreen onComplete={() => {}} />;
  }

  // PIN Login Required
  if (appState === 'pin-required') {
    return (
      <PinLogin
        onSuccess={handlePinSuccess}
        onForgotPin={handleForgotPin}
        onCancel={handlePinCancel}
      />
    );
  }

  // If we need the login screen but we are not there yet, avoid flashing protected content.
  if (appState === 'login-required' && location.pathname !== '/login' && !isBypassRoute) {
    return <SplashScreen onComplete={() => {}} />;
  }

  // Ready - render children (main app or login page)
  return <>{children}</>;
};

export default AppInitializer;
